import { useCallback, useEffect, useRef, useState } from 'react';
import {
  // standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  Check,
  CircleHelp,
  Database,
  LoaderCircle,
  LockKeyhole,
  Plus,
  TriangleAlert,
  X,
} from 'lucide-react';
import type { DestinationConnectionSummary } from '../shared/desktop-api';
import { CONNECTION_ADDED_EVENT, CONNECTIONS_CHANGED_EVENT } from './ComputerDoor';
import { OperatorErrorText, TechnicalDetails } from './TechnicalDetails';
import { readConnectionUse, removeConnectionQuestion } from './connection-use';
import { confirmAction } from './ConfirmActionDialog';

export type ConnectionIssueContext = 'load' | 'add' | 'check' | 'replace' | 'remove';
export type ConnectionIssue = { context: ConnectionIssueContext; message: string };

const desktop = () => window.capturePointDesktop;

const dateLabel = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/** The problem in a few words, for places with room for one short line. */
export function connectionProblem(connection: DestinationConnectionSummary): string {
  switch (connection.lastFailureCategory) {
    case 'unreachable':
      return 'Can’t reach Content Central';
    case 'certificate':
      return 'Its security certificate isn’t trusted';
    case 'key-refused':
      return 'Its key wasn’t accepted';
    case 'timeout':
      return 'Content Central didn’t answer in time';
    default: {
      const sentence = connection.lastFailureMessage
        ?.trim()
        .split(/(?<=\.)\s/)[0]
        ?.replace(/\.$/, '');
      return sentence || 'Check the connection or replace its key';
    }
  }
}

/** How a saved connection reads in a list: Ready, Needs attention (with why), or Not checked yet. */
export function connectionStateLine(connection: DestinationConnectionSummary): string {
  if (connection.lastFailureCode)
    return `Needs attention · ${connection.lastFailureMessage?.trim() || 'check the connection or replace its key'}`;
  if (connection.lastSuccessAt) return `Ready · checked ${dateLabel(connection.lastSuccessAt)}`;
  return 'Not checked yet';
}

/**
 * This computer's Content Central connections and the four things that can be
 * done to them. Settings, New job and Export all show the same list through
 * this one owner, so a connection added anywhere is the same connection.
 */
export function useConnectionManagement(
  onConnectionAdded?: (connection: DestinationConnectionSummary) => void,
) {
  const [connections, setConnections] = useState<DestinationConnectionSummary[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState(true);
  const [connectionIssue, setConnectionIssue] = useState<ConnectionIssue | null>(null);
  const [connectionBusy, setConnectionBusy] = useState<string | null>(null);
  const [connectionDraft, setConnectionDraft] = useState({ name: '', url: '' });
  const [showConnectionForm, setShowConnectionForm] = useState(false);
  const pageLive = useRef(true);
  const connectionReadAttempt = useRef(0);
  const connectionActionLatch = useRef(false);
  const onAdded = useRef(onConnectionAdded);
  onAdded.current = onConnectionAdded;

  useEffect(() => {
    pageLive.current = true;
    return () => {
      pageLive.current = false;
      connectionReadAttempt.current += 1;
    };
  }, []);

  const reloadConnections = useCallback(async (preserveIssue = false) => {
    const attempt = ++connectionReadAttempt.current;
    const api = desktop();
    if (!api) {
      setConnectionsLoading(false);
      setConnectionIssue({
        context: 'load',
        message: 'Connections are unavailable in this build.',
      });
      return;
    }
    setConnectionsLoading(true);
    try {
      const next = await api.listDestinationConnections();
      if (!pageLive.current || connectionReadAttempt.current !== attempt) return;
      setConnections(next);
      if (!preserveIssue) setConnectionIssue(null);
    } catch (reason) {
      if (pageLive.current && connectionReadAttempt.current === attempt)
        setConnectionIssue({
          context: 'load',
          message: reason instanceof Error ? reason.message : 'Connections could not be loaded.',
        });
    } finally {
      if (pageLive.current && connectionReadAttempt.current === attempt)
        setConnectionsLoading(false);
    }
  }, []);

  useEffect(() => {
    void reloadConnections();
  }, [reloadConnections]);
  // The status tray beside This computer re-reads when this list changes.
  useEffect(() => {
    window.dispatchEvent(new Event(CONNECTIONS_CHANGED_EVENT));
  }, [connections]);

  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const addConnection = async () => {
    const api = desktop();
    if (
      !api ||
      connectionActionLatch.current ||
      connectionBusy ||
      connectionsLoading ||
      !connectionDraft.name.trim() ||
      !connectionDraft.url.trim()
    )
      return;
    connectionActionLatch.current = true;
    setConnectionBusy('new');
    setConnectionIssue(null);
    try {
      const connected = await api.connectContentCentral({
        displayName: connectionDraft.name.trim(),
        baseUrl: connectionDraft.url.trim(),
      });
      if (!connected || !pageLive.current) return;
      setConnections((current) => [
        ...current.filter((item) => item.connectionId !== connected.connectionId),
        connected,
      ]);
      setConnectionDraft({ name: '', url: '' });
      setShowConnectionForm(false);
      // Whoever sent the operator here (a new job, Export) chooses this
      // connection for them when they go back.
      window.dispatchEvent(
        new CustomEvent(CONNECTION_ADDED_EVENT, { detail: connected.connectionId }),
      );
      onAdded.current?.(connected);
    } catch (reason) {
      if (pageLive.current)
        setConnectionIssue({
          context: 'add',
          message:
            reason instanceof Error ? reason.message : 'Content Central could not be connected.',
        });
    } finally {
      connectionActionLatch.current = false;
      if (pageLive.current) setConnectionBusy(null);
    }
  };

  const checkConnection = async (connectionId: string) => {
    const api = desktop();
    if (!api || connectionActionLatch.current || connectionBusy || connectionsLoading) return;
    connectionActionLatch.current = true;
    setConnectionBusy(connectionId);
    setConnectionIssue(null);
    try {
      const next = await api.recheckContentCentral(connectionId);
      if (pageLive.current)
        setConnections((current) =>
          current.map((item) => (item.connectionId === connectionId ? next : item)),
        );
    } catch (reason) {
      if (pageLive.current) {
        setConnectionIssue({
          context: 'check',
          message: reason instanceof Error ? reason.message : 'The connection check failed.',
        });
        void reloadConnections(true);
      }
    } finally {
      connectionActionLatch.current = false;
      if (pageLive.current) setConnectionBusy(null);
    }
  };

  const replaceConnectionKey = async (connectionId: string) => {
    const api = desktop();
    if (!api || connectionActionLatch.current || connectionBusy || connectionsLoading) return;
    connectionActionLatch.current = true;
    setConnectionBusy(connectionId);
    setConnectionIssue(null);
    try {
      const next = await api.replaceContentCentralKey(connectionId);
      if (next && pageLive.current)
        setConnections((current) =>
          current.map((item) => (item.connectionId === connectionId ? next : item)),
        );
    } catch (reason) {
      if (pageLive.current)
        setConnectionIssue({
          context: 'replace',
          message: reason instanceof Error ? reason.message : 'The replacement key was not saved.',
        });
    } finally {
      connectionActionLatch.current = false;
      if (pageLive.current) setConnectionBusy(null);
    }
  };

  const removeConnection = async (connection: DestinationConnectionSummary) => {
    const api = desktop();
    if (!api || connectionActionLatch.current || connectionBusy || connectionsLoading) return;
    connectionActionLatch.current = true;
    setConnectionBusy(connection.connectionId);
    setConnectionIssue(null);
    try {
      // Name the jobs whose export goes here, so the operator knows what pauses.
      // The engine leaves those jobs' export paused until a new destination is
      // chosen; it never sends to, or deletes from, Content Central.
      const jobNames = (await readConnectionUse(api)).get(connection.connectionId) ?? [];
      if (!pageLive.current) return;
      const confirmed = await confirmAction({
        ...removeConnectionQuestion(connection, jobNames),
        confirmLabel: 'Remove connection',
        destructive: true,
      });
      if (!confirmed || !pageLive.current) return;
      await api.removeContentCentral(connection.connectionId);
      if (pageLive.current)
        setConnections((current) =>
          current.filter((item) => item.connectionId !== connection.connectionId),
        );
    } catch (reason) {
      if (pageLive.current)
        setConnectionIssue({
          context: 'remove',
          message:
            reason instanceof Error ? reason.message : 'The connection could not be removed.',
        });
    } finally {
      connectionActionLatch.current = false;
      if (pageLive.current) setConnectionBusy(null);
    }
  };

  return {
    connections,
    connectionsLoading,
    connectionIssue,
    connectionBusy,
    connectionDraft,
    showConnectionForm,
    setConnectionDraft,
    setShowConnectionForm,
    reloadConnections,
    addConnection,
    checkConnection,
    replaceConnectionKey,
    removeConnection,
  };
}

export type ConnectionManagementState = ReturnType<typeof useConnectionManagement>;

/** The connection list with its form and actions, drawn from one owner's state. */
export function ConnectionManagement({ state }: { state: ConnectionManagementState }) {
  const {
    connections,
    connectionsLoading: loading,
    connectionIssue,
    connectionBusy: busy,
    connectionDraft: draft,
    showConnectionForm: showForm,
    setConnectionDraft,
    setShowConnectionForm,
  } = state;
  const error = connectionIssue?.message ?? '';
  return (
    <section className="card computer-connections-card">
      <header className="computer-connections-head">
        <div>
          <h2>
            <Database size={18} /> Content Central
          </h2>
          <p>One saved connection can be reused by any job on this computer.</p>
        </div>
        <button
          type="button"
          className="secondary-button small"
          data-telemetry="content-central-new"
          onClick={() => setShowConnectionForm((current) => !current)}
          disabled={loading || busy !== null}
          aria-expanded={showForm}
        >
          {showForm ? <X size={13} aria-hidden="true" /> : <Plus size={13} aria-hidden="true" />}{' '}
          {showForm ? 'Close' : 'New connection'}
        </button>
      </header>

      {showForm && (
        <div className="computer-connection-form" aria-busy={busy === 'new'}>
          <label>
            <span>Connection name</span>
            <input
              disabled={loading || busy !== null}
              value={draft.name}
              maxLength={120}
              placeholder="Local Content Central"
              onChange={(event) => setConnectionDraft({ ...draft, name: event.target.value })}
            />
          </label>
          <label>
            <span>Content Central address</span>
            <input
              disabled={loading || busy !== null}
              type="url"
              value={draft.url}
              placeholder="https://documents.example.com/ContentCentral"
              onChange={(event) => setConnectionDraft({ ...draft, url: event.target.value })}
            />
          </label>
          <button
            type="button"
            className="primary-button small"
            data-telemetry="content-central-connect"
            onClick={() => void state.addConnection()}
            disabled={loading || busy !== null || !draft.name.trim() || !draft.url.trim()}
          >
            {busy === 'new' ? (
              <>
                <LoaderCircle className="spin" size={13} /> Connecting…
              </>
            ) : (
              <>
                <LockKeyhole size={13} /> Enter key securely
              </>
            )}
          </button>
          {busy === 'new' && (
            <span className="visually-hidden" role="status">
              Connecting to Content Central…
            </span>
          )}
        </div>
      )}

      {error && (
        <div
          className="computer-connection-error"
          role="alert"
          data-telemetry={
            connectionIssue && ['add', 'check', 'replace'].includes(connectionIssue.context)
              ? 'error:content-central-failed'
              : connectionIssue?.context === 'remove'
                ? 'error:connection-remove-failed'
                : 'error:connections-load-failed'
          }
        >
          <TriangleAlert size={15} />
          <OperatorErrorText message={error} />
          <button
            type="button"
            className="quiet-button small"
            onClick={() => void state.reloadConnections()}
            disabled={loading || busy !== null}
          >
            Refresh connections
          </button>
        </div>
      )}

      {loading ? (
        <p className="computer-connections-loading" role="status">
          <LoaderCircle className="spin" size={15} /> Reading saved connections…
        </p>
      ) : connections.length === 0 && !error ? (
        <div className="computer-connections-empty">
          <Database size={24} />
          <div>
            <strong>No connections yet</strong>
            <span>
              Add Content Central here once, then choose it when setting up a job or export.
            </span>
          </div>
        </div>
      ) : (
        <div className="computer-connection-list">
          {connections.map((connection) => {
            const working = busy === connection.connectionId;
            const tone = connection.lastFailureCode
              ? 'attention'
              : connection.lastSuccessAt
                ? 'ready'
                : 'unchecked';
            return (
              <article
                key={connection.connectionId}
                className={`computer-connection-row ${tone}`}
                aria-busy={working}
              >
                <span className="computer-connection-state" aria-hidden="true">
                  {working ? (
                    <LoaderCircle className="spin" size={16} />
                  ) : connection.lastFailureCode ? (
                    <TriangleAlert size={16} />
                  ) : connection.lastSuccessAt ? (
                    <Check size={16} />
                  ) : (
                    <CircleHelp size={16} />
                  )}
                </span>
                <div className="computer-connection-copy">
                  <strong>{connection.displayName}</strong>
                  <span>{connection.baseUrl}</span>
                  <small aria-live="polite">
                    {working
                      ? 'Updating this connection…'
                      : connection.lastFailureCode
                        ? connectionStateLine(connection)
                        : `${connectionStateLine(connection)} · key ${connection.redactedKeyPrefix}`}
                  </small>
                  {!working && connection.lastFailureCode && (
                    <TechnicalDetails detail={connection.lastFailureDetail} />
                  )}
                </div>
                <div className="computer-connection-actions">
                  <button
                    type="button"
                    className="quiet-button small"
                    data-telemetry="content-central-check"
                    disabled={busy !== null}
                    onClick={() => void state.checkConnection(connection.connectionId)}
                  >
                    Check
                  </button>
                  <button
                    type="button"
                    className="quiet-button small"
                    data-telemetry="content-central-replace-key"
                    disabled={busy !== null}
                    onClick={() => void state.replaceConnectionKey(connection.connectionId)}
                  >
                    Replace key
                  </button>
                  <button
                    type="button"
                    className="quiet-button small danger"
                    data-telemetry="content-central-remove"
                    disabled={busy !== null}
                    onClick={() => void state.removeConnection(connection)}
                  >
                    Remove
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

/** This computer › Connections, for a window opened from a job (New job, Export). */
export function ConnectionsPanel({
  onConnectionAdded,
}: {
  onConnectionAdded?: (connection: DestinationConnectionSummary) => void;
}) {
  const state = useConnectionManagement(onConnectionAdded);
  return (
    <div className="computer-section connections-window-body">
      <ConnectionManagement state={state} />
    </div>
  );
}
